import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createDisplayAndEventsRequest,
  getCustomerData,
  getDivisionData,
  getDisplayAndEventsAgencyPurposeMaster,
  getDisplayAndEventsHistory,
  getDisplayAndEventsRequests
} from 'Service/ApiService';
import { showAlert } from 'utils/ErrorMessage';

const getTodayDate = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${today.getFullYear()}-${month}-${day}`;
};

export const getInitialDisplayAndEventsForm = (user = {}) => ({
  userCode: user?.userCode || '',
  eventname: '',
  registersrno: '',
  division: '',
  requestdate: getTodayDate(),
  startdate: '',
  enddate: '',
  eventtype: '',
  customerCode: '',
  expectedsalesqty: '',
  expectedsalesamount: '',
  expecteddealersno: '',
  expectedretailersno: '',
  customeravgdaystay: '',
  expectedagentsno: '',
  staffavgdaystay: '',
  expectedstaffno: '',
  hotelcostperperson: '',
  hoteltotalcost: '',
  travelcostperperson: '',
  traveltotalcost: '',
  displaycost: '',
  logisticscost: '',
  photovideoshootcost: '',
  totalestimatedcost: '',
  expenditurepercentage: '',
  expenditurepercustomer: '',
  existingscheme: '',
  additionalscheme: '',
  schemerate: '',
  schemefrom: '',
  schemeupto: '',
  dresscostperperson: '',
  dressstaffcount: '',
  dressstafftotalcost: '',
  dressagentscount: '',
  dressagentstotalcost: '',
  giftitem: '',
  giftcostperitem: '',
  giftquantity: '',
  gifttotalcost: ''
});

export const initialDisplayAndEventsFilters = {
  eventname: '',
  division: '',
  fromDate: '',
  toDate: '',
  status: ''
};

const normalizeEventRequest = (request = {}) => ({
  showid: request.showid || '',
  eventid: request.eventid || '',
  eventname: request.eventname || '',
  registersrno: request.registersrno || '',
  division: request.division || '',
  requestdate: request.requestdate || '',
  startdate: request.startdate || '',
  enddate: request.enddate || '',
  eventtype: request.eventtype || '',
  customerCode: request.customerCode || request.customercode || '',
  expectedsalesqty: request.expectedsalesqty || '',
  expectedsalesamount: request.expectedsalesamount || '',
  expecteddealersno: request.expecteddealersno || '',
  expectedretailersno: request.expectedretailersno || '',
  customeravgdaystay: request.customeravgdaystay || '',
  expectedagentsno: request.expectedagentsno || '',
  staffavgdaystay: request.staffavgdaystay || '',
  expectedstaffno: request.expectedstaffno || '',
  hotelcostperperson: request.hotelcostperperson || '',
  hoteltotalcost: request.hoteltotalcost || '',
  travelcostperperson: request.travelcostperperson || '',
  traveltotalcost: request.traveltotalcost || '',
  displaycost: request.displaycost || '',
  logisticscost: request.logisticscost || '',
  photovideoshootcost: request.photovideoshootcost || '',
  totalestimatedcost: request.totalestimatedcost || '',
  expenditurepercentage: request.expenditurepercentage || '',
  expenditurepercustomer: request.expenditurepercustomer || '',
  existingscheme: request.existingscheme ?? '',
  additionalscheme: request.additionalscheme ?? '',
  schemerate: request.schemerate || '',
  schemefrom: request.schemefrom || '',
  schemeupto: request.schemeupto || '',
  dresscostperperson: request.dresscostperperson || '',
  dressstaffcount: request.dressstaffcount || '',
  dressstafftotalcost: request.dressstafftotalcost || '',
  dressagentscount: request.dressagentscount || '',
  dressagentstotalcost: request.dressagentstotalcost || '',
  giftitem: request.giftitem || '',
  giftcostperitem: request.giftcostperitem || '',
  giftquantity: request.giftquantity || '',
  gifttotalcost: request.gifttotalcost || '',
  agencies: Array.isArray(request.agencies) ? request.agencies : [],
  status: request.status || request.lastApprovalStatus || '',
  requestsubmittedby: request.requestsubmittedby || '',
  lastAction: request.lastAction || '',
  lastActionByRole: request.lastActionByRole || '',
  lastActionDate: request.lastActionDate || '',
  lastApprovalStatus: request.lastApprovalStatus || '',
  createdat: request.createdat || '',
  updatedat: request.updatedat || ''
});

const normalizeActionHistory = (item = {}) => ({
  id: item.approvalid || `${item.eventid || ''}-${item.actiondate || item.createdat || ''}`,
  action: item.actiontype || '',
  actionBy: item.actionByUser || '',
  role: item.actionByRole || '',
  status: item.status || '',
  remarks: item.remarks || '',
  actionDate: item.actiondate || item.createdat || ''
});

const buildDivisionOption = (division = {}) => {
  const value = String(division?.dv || division?.value || '').trim();
  const label = String(division?.divisionName || division?.label || '').trim();
  const apiValue = value && label ? `${value}-${label}` : value || label;

  return {
    value,
    label: label && value ? `${label} (${value})` : label || value,
    apiValue
  };
};

const buildCustomerOption = (customer = {}) => {
  const customerCode = String(customer?.partyCode || '').trim();
  const customerName = String(customer?.partyName || '').trim();
  const city = String(customer?.city || '').trim();

  return {
    value: customerCode,
    label: [customerName, city, customerCode].filter(Boolean).join('-')
  };
};

const buildAgencyPurposeOption = (purpose = {}) => {
  const purposeName = String(purpose?.purposeName || '').trim();

  return {
    value: purposeName,
    label: purposeName
  };
};

const DispayAndEventsController = (page = 0) => {
  const loggedInUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const loggedInUserCode = String(loggedInUser?.userCode || '').trim();
  const [agencyPurposeOptions, setAgencyPurposeOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [eventRequests, setEventRequests] = useState([]);
  const [filters, setFilters] = useState(initialDisplayAndEventsFilters);
  const [totalRows, setTotalRows] = useState(0);
  const [actionHistory, setActionHistory] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [isAgencyPurposeOptionsLoading, setIsAgencyPurposeOptionsLoading] = useState(false);
  const [isDivisionOptionsLoading, setIsDivisionOptionsLoading] = useState(false);
  const [isCustomerOptionsLoading, setIsCustomerOptionsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadDivisionOptions = async () => {
      setIsDivisionOptionsLoading(true);

      try {
        const response = await getDivisionData();

        if (!isMounted) {
          return;
        }

        if (!response || (response.success !== true && response.success !== 'true')) {
          throw new Error(response?.msg || 'Failed to load divisions.');
        }

        const nextOptions = Array.isArray(response?.data)
          ? response.data.map(buildDivisionOption).filter((option) => option.value)
          : [];

        setDivisionOptions(nextOptions);
      } catch (error) {
        if (isMounted) {
          setDivisionOptions([]);
          showAlert(error?.message || 'Failed to load divisions.');
        }
      } finally {
        if (isMounted) {
          setIsDivisionOptionsLoading(false);
        }
      }
    };

    loadDivisionOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCustomerOptions = async () => {
      setIsCustomerOptionsLoading(true);

      try {
        const response = await getCustomerData(loggedInUserCode, '');

        if (!isMounted) {
          return;
        }

        if (!response || (response.success !== true && response.success !== 'true')) {
          throw new Error(response?.msg || 'Failed to load customers.');
        }

        const nextOptions = Array.isArray(response?.customerList)
          ? response.customerList.map(buildCustomerOption).filter((option) => option.value)
          : [];

        setCustomerOptions(nextOptions.filter((option, index, options) => (
          options.findIndex((item) => item.value === option.value) === index
        )));
      } catch (error) {
        if (isMounted) {
          setCustomerOptions([]);
          showAlert(error?.message || 'Failed to load customers.');
        }
      } finally {
        if (isMounted) {
          setIsCustomerOptionsLoading(false);
        }
      }
    };

    loadCustomerOptions();

    return () => {
      isMounted = false;
    };
  }, [loggedInUserCode]);

  useEffect(() => {
    let isMounted = true;

    const loadAgencyPurposeOptions = async () => {
      setIsAgencyPurposeOptionsLoading(true);

      try {
        const response = await getDisplayAndEventsAgencyPurposeMaster();

        if (!isMounted) {
          return;
        }

        if (!response || (response.success !== true && response.success !== 'true')) {
          throw new Error(response?.msg || 'Failed to load agency purposes.');
        }

        const nextOptions = Array.isArray(response?.data)
          ? response.data.map(buildAgencyPurposeOption).filter((option) => option.value)
          : [];

        setAgencyPurposeOptions(nextOptions);
      } catch (error) {
        if (isMounted) {
          setAgencyPurposeOptions([]);
          showAlert(error?.message || 'Failed to load agency purposes.');
        }
      } finally {
        if (isMounted) {
          setIsAgencyPurposeOptionsLoading(false);
        }
      }
    };

    loadAgencyPurposeOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadEventRequests = useCallback(async () => {
    setIsTableLoading(true);

    try {
      const response = await getDisplayAndEventsRequests(loggedInUserCode, {
        ...filters,
        page: page + 1,
        pageSize: 100
      });

      if (!response || (response.success !== true && response.success !== 'true')) {
        throw new Error(response?.msg || 'Failed to load display and events requests.');
      }

      const nextRequests = Array.isArray(response?.data)
        ? response.data.map(normalizeEventRequest)
        : [];

      setEventRequests(nextRequests);
      setTotalRows(Number(response?.totalRows || nextRequests.length || 0));
    } catch (error) {
      setEventRequests([]);
      setTotalRows(0);
      showAlert(error?.message || 'Failed to load display and events requests.');
    } finally {
      setIsTableLoading(false);
    }
  }, [filters, loggedInUserCode, page]);

  useEffect(() => {
    const debounceTimer = setTimeout(loadEventRequests, 300);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [loadEventRequests, refreshKey]);

  const submitEventRequest = async (formValues) => {
    setIsSubmitting(true);

    try {
      const response = await createDisplayAndEventsRequest(formValues);

      if (!response || (response.success !== true && response.success !== 'true')) {
        throw new Error(response?.msg || 'Failed to submit event request.');
      }

      setRefreshKey((prev) => prev + 1);
      return response;
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadActionHistory = async (eventId) => {
    if (!eventId) {
      setActionHistory([]);
      setHistoryError('Event id is missing.');
      return;
    }

    setIsHistoryLoading(true);
    setHistoryError('');

    try {
      const response = await getDisplayAndEventsHistory(eventId);

      if (!response || (response.success !== true && response.success !== 'true')) {
        throw new Error(response?.msg || 'Failed to load history.');
      }

      const nextHistory = Array.isArray(response?.data)
        ? response.data.map(normalizeActionHistory)
        : [];

      setActionHistory(nextHistory);
    } catch (error) {
      setActionHistory([]);
      setHistoryError(error?.message || 'Failed to load history.');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const clearActionHistory = () => {
    setActionHistory([]);
    setHistoryError('');
    setIsHistoryLoading(false);
  };

  return {
    actionHistory,
    agencyPurposeOptions,
    clearActionHistory,
    customerOptions,
    divisionOptions,
    eventRequests,
    filters,
    historyError,
    isAgencyPurposeOptionsLoading,
    isDivisionOptionsLoading,
    isCustomerOptionsLoading,
    isHistoryLoading,
    isSubmitting,
    isTableLoading,
    loadActionHistory,
    loggedInUser,
    loggedInUserCode,
    refreshList: loadEventRequests,
    setFilters,
    submitEventRequest,
    totalRows
  };
};

export default DispayAndEventsController;
